import { observer } from "mobx-react-lite";
import Layout from "../../components/layout/Layout";
import { useStore } from "../../stores/root-store-context";
import {
  Button,
  Dropdown,
  Flex,
  Form,
  Input,
  Modal,
  Spin,
  Space,
  message,
} from "antd";
import type { MenuProps } from "antd";
import { DownloadOutlined, DownOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import UserComponent from "./UserComponent";
import { Employee } from "../../models/Employee";
import { downloadBlob, parseContentDispositionFilename } from "../../helpers/downloadBlob";

type ResumeTemplate = "cv" | "profile";

const Account = observer(() => {
  const { employeesStore } = useStore();
  const navigate = useNavigate();
  const userPage = useRef(null);
  const { id } = useParams();
  const { t, i18n } = useTranslation();

  const [isModalPassword, setIsModalPassword] = useState(false);
  const [isModalEmail, setIsModalEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDownloadingResume, setIsDownloadingResume] = useState(false);
  const [user, setUser] = useState<Employee>();

  const showModalPassword = () => {
    setIsModalPassword(true);
  };

  const handleOkPassword = () => {
    setIsModalPassword(false);
  };

  const handleCancelPassword = () => {
    setIsModalPassword(false);
  };

  const showModalEmail = () => {
    setIsModalEmail(true);
  };

  const handleOkEmail = () => {
    setIsModalEmail(false);
  };

  const handleCancelEmail = () => {
    setIsModalEmail(false);
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      employeesStore.ApiStore.EmployeesApiClient.getUserById(id)
        .then((result) => {
          setUser(result);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      navigate("/404");
    }
  }, [id]);

  /**
   * The PDF is rendered on the server (QuestPDF) - see CompanyUsersController.ExportResume.
   * The browser only downloads it, so there is no html2canvas rasterization here any more:
   * the file is ~150 KB of real, selectable text instead of several megabytes of bitmap.
   */
  const downloadResume = async (template: ResumeTemplate) => {
    if (!user?.id || isDownloadingResume) return;

    setIsDownloadingResume(true);
    try {
      const { blob, contentDisposition } =
        await employeesStore.ApiStore.EmployeesApiClient.exportResumePdf(
          String(user.id),
          template,
          i18n.language
        );

      const fallbackName = `${user.lastName ?? ""}_${user.firstName ?? ""}_${
        template === "profile" ? "Profile" : "CV"
      }.pdf`.replace(/^_+/, "");

      downloadBlob(blob, parseContentDispositionFilename(contentDisposition, fallbackName));
    } catch (error) {
      console.error("Resume download failed:", error);
      message.error(t("ResumeDownloadFailed"));
    } finally {
      setIsDownloadingResume(false);
    }
  };

  const resumeTemplateItems: MenuProps["items"] = [
    { key: "cv", label: t("resume.templateCv") },
    { key: "profile", label: t("resume.templateProfile") },
  ];

  return (
    <>
      <Layout headerStyle={1} footerStyle={2}>
        <div className="profiles-font-settings">
          <Spin spinning={loading} style={{ minHeight: 600 }}>
            <Flex className="container" gap={10} style={{ marginTop: 20, display: "flex", justifyContent: "space-between" }}>
              <Button
                style={{ float: "left", borderRadius: 0 }}
                onClick={() => {
                  navigate(-1);
                }}
              >
                {t("back")}
              </Button>
              <Space>
                {/* `icon` styles the dropdown trigger, not the main button - the download glyph
                    belongs on the label, and the trigger keeps a conventional caret. */}
                <Dropdown.Button
                  type="primary"
                  icon={<DownOutlined />}
                  loading={isDownloadingResume}
                  disabled={!user || isDownloadingResume}
                  menu={{
                    items: resumeTemplateItems,
                    onClick: ({ key }) => downloadResume(key as ResumeTemplate),
                  }}
                  onClick={() => downloadResume("cv")}
                >
                  <DownloadOutlined /> {t("DownloadResume")}
                </Dropdown.Button>
              </Space>
            </Flex>
            <Flex
              className="container"
              gap={10}
              style={{ marginTop: 20, marginBottom: 20 }}
              vertical
              ref={userPage}
            >
              {user && <UserComponent user={user} />}
            </Flex>
          </Spin>
        </div>
        <Modal
          title={t("enterNewEmail")}
          open={isModalEmail}
          onOk={handleOkEmail}
          onCancel={handleCancelEmail}
        >
          <Form>
            <Form.Item<any>
              name="Email"
              style={{ width: "100%" }}
              rules={[{ required: true }]}
            >
              <Input placeholder={t("emailPlaceholder")} pattern="^[^@\s]+@[^@\s]+\.[^@\s]+$" />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={t("enterNewPassword")}
          open={isModalPassword}
          onOk={handleOkPassword}
          onCancel={handleCancelPassword}
        >
          <Form>
            <Form.Item<any>
              name="Password"
              style={{ width: "100%" }}
              rules={[{ required: true }]}
            >
              <Input placeholder={t("passwordPlaceholder")} type="password" />
            </Form.Item>
          </Form>
        </Modal>
      </Layout>
    </>
  );
});
export default Account;
